import React, {Component} from 'react';
import ReactDOM from 'react-dom';
import {connect} from 'react-redux';
import cookie from 'react-cookies';
import {store} from '../../../../helper/store';
import {deleteMultipleAnnotations, updateSelectedAnnotationsList} from '../../../../redux_actions/maps.state.action';
import {openTopApp, updateActiveMap, updateGammaState} from '../../../../redux_actions/gamma.state.action';
import {expandAnnotation} from '../../../../redux_actions/triggers.action';
import {annotationsApp} from '../../app_maker';
import {AnnotationsConstants} from '../../../../utils/const';
import {aclPermissions, hasPermissions} from '../../../../const/ACLPermissions';
import {getLoggedInUserId, isSuperuser} from '../../../../utils/utils';
import {notifyWarning} from '../../utils/display.notification';
import {
    annoLabelFont,
    checkAbstractAppPermission,
    getAbstractConfigValue,
    showAnnotationsLabel,
} from '../../../utils/abstract_app_maker';
import {parseBounds, pathLength, pathMidpoint, uprightAngle} from './annotationGeometry';
import DrawInteractionOSD from './DrawInteractionOSD';
import {forwardWheelToViewer, panViewerByPixels} from './viewerPointer';

const MARKED_COLOR = '#7CFC00';
const HIT_STROKE_WIDTH = 16;
// Near-invisible paint: fully transparent strokes are skipped by SVG hit-testing in some browsers.
const HIT_PAINT = 'rgba(0,0,0,0.001)';
const TEXT_FONT_FAMILY = '"Open Sans", "Helvetica", "sans-serif"';
const DRAG_THRESHOLD_PX = 5;

let measureCtx = null;
const measureTextWidth = (text, font) => {
    if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
    if (!measureCtx) return text.length * 11;
    measureCtx.font = font;
    return measureCtx.measureText(text).width;
};

/**
 * SVG overlay for the annotations app's annotations, portalled into the OSD container and
 * re-projected on every viewport change. Styles follow BaseAnnotationLayer (OL): white halo,
 * dashed strokes for a superuser's own annotations, selected = halo + black + yellow, titles
 * along the path, "Label" annotations as text only, and arrows with a ">" head.
 *
 * Pointer events pass through to OSD except on annotations (click selects, drag pans). While a
 * draw tool is active on this map, DrawInteractionOSD takes all pointer input.
 */
class AnnotationOverlayOSD extends Component {
    constructor(props) {
        super(props);
        this.overlayDivRef = React.createRef();
        this.annotationsGroupRef = React.createRef();
        this._osdHandlers = {};
        this._osdHandlersViewer = null;
        this._resizeObserver = null;
        this._wheelTarget = null;
        this._dragTarget = null;
        this._dragState = null;
        this.showLabels = checkAbstractAppPermission(showAnnotationsLabel);
        this.labelFontSize = getAbstractConfigValue(annoLabelFont, 20);
        this.state = {containerWidth: 0, containerHeight: 0, tick: 0};
    }

    componentDidMount() {
        this._attachOSDHandlers();
        this._attachResizeObserver();
        document.addEventListener('keydown', this._onKeyDown);
        this._attachForwarders();
    }

    componentWillUnmount() {
        this._detachOSDHandlers();
        this._detachForwarders();
        this._detachResizeObserver();
        document.removeEventListener('keydown', this._onKeyDown);
        if (this._repaintRaf != null) cancelAnimationFrame(this._repaintRaf);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.slidemap !== this.props.slidemap) {
            this._detachOSDHandlers();
            this._attachOSDHandlers();
        }
        if (this._getViewer()?.container && this.overlayDivRef.current && !this._resizeObserver) {
            this._attachResizeObserver();
        }
        // The ResizeObserver can miss the first size, which would leave the SVG unmounted.
        if (this.isDrawingHere() && !(prevProps.activeDrawTool && prevProps.isActiveMap)) this._syncOverlaySize();
        this._attachForwarders();
    }

    _getViewer = () => (this.props.slidemap?._isOpenSeadragon ? this.props.slidemap : null);

    isDrawingHere = () => !!this.props.activeDrawTool && this.props.isActiveMap;

    // ------------------------------------------------------------------ OSD events

    _repaint = () => {
        if (this._repaintRaf != null) return;
        this._repaintRaf = requestAnimationFrame(() => {
            this._repaintRaf = null;
            this.setState(s => ({tick: s.tick + 1}));
        });
    };

    _repaintNow = () => this.setState(s => ({tick: s.tick + 1}));

    _attachOSDHandlers = () => {
        const viewer = this._getViewer();
        if (!viewer) return;
        this._osdHandlers = {
            'viewport-change': this._repaint,
            'animation': this._repaint,
            'animation-finish': this._repaintNow,
            'open': this._repaintNow,
            'resize': this._repaintNow,
            'rotate': this._repaintNow,
            'canvas-click': this._onCanvasClick,
        };
        Object.entries(this._osdHandlers).forEach(([event, handler]) => viewer.addHandler(event, handler));
        this._osdHandlersViewer = viewer;
    };

    _detachOSDHandlers = () => {
        const viewer = this._osdHandlersViewer;
        if (viewer) {
            Object.entries(this._osdHandlers).forEach(([event, handler]) => {
                try {
                    viewer.removeHandler(event, handler);
                } catch (e) { /* viewer already destroyed */ }
            });
        }
        this._osdHandlers = {};
        this._osdHandlersViewer = null;
    };

    _syncOverlaySize = () => {
        const el = this.overlayDivRef.current;
        if (!el) return;
        const width = Math.round(el.clientWidth || el.offsetWidth || 0);
        const height = Math.round(el.clientHeight || el.offsetHeight || 0);
        if (width > 0 && height > 0 && (width !== this.state.containerWidth || height !== this.state.containerHeight)) {
            this.setState({containerWidth: width, containerHeight: height});
        }
    };

    _attachResizeObserver = () => {
        const el = this.overlayDivRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        this._resizeObserver = new ResizeObserver(this._syncOverlaySize);
        this._resizeObserver.observe(el);
        this._syncOverlaySize();
    };

    _detachResizeObserver = () => {
        this._resizeObserver?.disconnect();
        this._resizeObserver = null;
    };

    // ------------------------------------------------------------------ wheel + drag on annotations

    /** Annotations take pointer events, so wheel and drags on them are handed to OSD. */
    _attachForwarders = () => {
        const el = this.annotationsGroupRef.current;
        if (el === this._wheelTarget) return;
        this._detachForwarders();
        if (!el) return;
        el.addEventListener('wheel', this._forwardWheel, {passive: false});
        el.addEventListener('mousedown', this._onDragMouseDown, true);
        this._wheelTarget = el;
        this._dragTarget = el;
    };

    _detachForwarders = () => {
        this._wheelTarget?.removeEventListener('wheel', this._forwardWheel);
        this._dragTarget?.removeEventListener('mousedown', this._onDragMouseDown, true);
        document.removeEventListener('mousemove', this._onDragMouseMove, true);
        document.removeEventListener('mouseup', this._onDragMouseUp, true);
        this._wheelTarget = null;
        this._dragTarget = null;
        this._dragState = null;
    };

    _forwardWheel = (e) => forwardWheelToViewer(this._getViewer(), e);

    _onDragMouseDown = (e) => {
        if (this.props.activeDrawTool || e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        this._dragState = {isDragging: false, startX: e.clientX, startY: e.clientY, lastX: e.clientX, lastY: e.clientY,
            target: e.target, shiftKey: e.shiftKey};
        document.addEventListener('mousemove', this._onDragMouseMove, true);
        document.addEventListener('mouseup', this._onDragMouseUp, true);
    };

    _onDragMouseMove = (e) => {
        const drag = this._dragState;
        if (!drag) return;
        if (!drag.isDragging && Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY) <= DRAG_THRESHOLD_PX) return;
        e.preventDefault();
        e.stopPropagation();
        drag.isDragging = true;
        panViewerByPixels(this._getViewer(), drag.lastX - e.clientX, drag.lastY - e.clientY);
        drag.lastX = e.clientX;
        drag.lastY = e.clientY;
    };

    _onDragMouseUp = (e) => {
        const drag = this._dragState;
        document.removeEventListener('mousemove', this._onDragMouseMove, true);
        document.removeEventListener('mouseup', this._onDragMouseUp, true);
        this._dragState = null;
        if (!drag) return;
        if (drag.isDragging) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        const id = drag.target?.closest?.('[data-annotation-id]')?.getAttribute('data-annotation-id');
        const annotation = id != null && this.getAnnotations().find(a => String(a.id) === id);
        if (annotation) this._onAnnotationClick(annotation, drag.shiftKey);
    };

    // ------------------------------------------------------------------ selection (OL Select semantics)

    getAnnotations = () => (this.props.annotations || []).filter(a => a.anno_drawer === annotationsApp.drawer);

    _isSelected = (id) => (this.props.selectedAnnotations || []).some(selected => String(selected) === String(id));

    _deselectAll = () => {
        const {mapId, selectedAnnotations} = this.props;
        if (selectedAnnotations?.length) store.dispatch(updateSelectedAnnotationsList(mapId, [...selectedAnnotations], false));
    };

    /** Click selects only this annotation; Shift+click toggles it (OL `toggleCondition`). */
    _onAnnotationClick = (annotation, toggle) => {
        const {mapId} = this.props;
        store.dispatch(updateActiveMap(mapId));
        if (toggle && this._isSelected(annotation.id)) {
            store.dispatch(updateSelectedAnnotationsList(mapId, [annotation.id], false));
            return;
        }
        if (!toggle) {
            const others = (this.props.selectedAnnotations || []).filter(id => String(id) !== String(annotation.id));
            if (others.length) store.dispatch(updateSelectedAnnotationsList(mapId, others, false));
        }
        store.dispatch(openTopApp(annotationsApp));
        store.dispatch(expandAnnotation(annotation.id));
        store.dispatch(updateSelectedAnnotationsList(mapId, [annotation.id], true));
    };

    /** A quick click on empty canvas deselects, as OL Select does. */
    _onCanvasClick = (event) => {
        if (!event.quick || this.props.activeDrawTool || event.originalEvent?.shiftKey) return;
        this._deselectAll();
    };

    // ------------------------------------------------------------------ keyboard

    _onKeyDown = (e) => {
        if (!this.props.isActiveMap) return;
        const target = e.target;
        if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable) return;

        if (e.key === 'Escape') {
            if (this.props.activeDrawTool) store.dispatch(updateGammaState({activeDrawTool: null}));
            this._deselectAll();
        } else if (e.key === 'Delete' && this.props.selectedAnnotations?.length) {
            e.preventDefault();
            this._deleteSelected();
        }
    };

    /** Same permission rules as AnnotationsExpandableList.deleteAnnotations. */
    _deleteSelected = () => {
        const {mapId, slide, selectedAnnotations} = this.props;
        if (!slide?.id) return;
        const selected = (this.props.annotations || []).filter(a => this._isSelected(a.id));
        if (!selected.length) return;

        let toDelete;
        if (hasPermissions([aclPermissions.delete_other_user_annotation])) {
            toDelete = selected;
        } else if (hasPermissions([aclPermissions.delete_own_annotation])) {
            toDelete = selected.filter(a => a.creator === getLoggedInUserId());
            notifyWarning('anno', "You don't have permission to delete other user annotations! Only your annotations will be deleted.");
        } else {
            notifyWarning('anno', "You don't have permission to delete annotations!");
            return;
        }
        if (!toDelete.length) return;
        store.dispatch(deleteMultipleAnnotations(mapId, slide.id, toDelete));
        store.dispatch(updateSelectedAnnotationsList(mapId, [...selectedAnnotations], false));
    };

    // ------------------------------------------------------------------ styles (BaseAnnotationLayer.style)

    getStyle = (annotation, isSelected) => {
        const isLabel = annotation.tool_type === AnnotationsConstants.TOOL_TYPE.LABEL;
        const isArrow = annotation.tool_type === AnnotationsConstants.TOOL_TYPE.ARROW;
        const isBlackBorder = annotation.title === 'Border' && annotation.color === '#000';
        const isROI = (annotation.title || '').toLowerCase() === AnnotationsConstants.ROI_TITLE.toLowerCase();
        const metaWidth = annotation.meta?.width;
        let width = metaWidth != null ? Number(metaWidth) : 2;
        let haloWidth = metaWidth != null ? 2 * Number(metaWidth) : 2;
        if (isBlackBorder) {
            width = 1;
            haloWidth = 0;
        }
        const color = annotation.marked ? MARKED_COLOR
            : isROI ? AnnotationsConstants.ROI_COLOR
                : annotation.color || AnnotationsConstants.DEFAULT_ANNO_COLOR;
        const dashed = isSuperuser() && String(annotation.creator) === String(cookie.loadAll().user_id);
        const hasTitle = !!annotation.title && annotation.shape !== AnnotationsConstants.CIRCLE;
        const showText = hasTitle && !isArrow && (isLabel || (this.showLabels && !(isBlackBorder && !isSelected)));
        return {color, width, haloWidth, dashed, isLabel, isArrow, showText};
    };

    // ------------------------------------------------------------------ projection

    toScreen = (point) => this._getViewer()?.olToPixel(point) ?? [0, 0];

    toOl = (screenPoint) => this._getViewer()?.pixelToOl(screenPoint) ?? [0, 0];

    /** Screen path (open for lines, closed ring for polygons) and the drawable element props. */
    project = (annotation, bounds) => {
        if (annotation.shape === AnnotationsConstants.CIRCLE) {
            const [center, radius] = bounds;
            const [cx, cy] = this.toScreen(center);
            const [ex, ey] = this.toScreen([center[0] + radius, center[1]]);
            return {kind: 'circle', cx, cy, r: Math.hypot(ex - cx, ey - cy)};
        }
        if (annotation.shape === AnnotationsConstants.LINE) {
            return {kind: 'line', points: bounds.map(this.toScreen)};
        }
        if (annotation.shape === AnnotationsConstants.POLYGON) {
            return {kind: 'polygon', rings: bounds.map(ring => ring.map(this.toScreen))};
        }
        return null;
    };

    // ------------------------------------------------------------------ rendering

    renderShape = (shape, props) => {
        if (shape.kind === 'circle') return <circle cx={shape.cx} cy={shape.cy} r={shape.r} {...props}/>;
        if (shape.kind === 'line') return <polyline points={shape.points.map(p => p.join(',')).join(' ')} fill="none" {...props}/>;
        const d = shape.rings.map(ring => `M ${ring.map(p => p.join(',')).join(' L ')} Z`).join(' ');
        return <path d={d} {...props}/>;
    };

    renderArrowHead = (points, color) => {
        if (points.length < 2) return null;
        const [x1, y1] = points[points.length - 2];
        const [x2, y2] = points[points.length - 1];
        const angle = Math.atan2(y2 - y1, x2 - x1) * 180 / Math.PI;
        return (
            <text x={0} y={0} fontSize={35} fontWeight="bold" fill={color} textAnchor="middle" dominantBaseline="central"
                  transform={`translate(${x2},${y2}) rotate(${angle})`} pointerEvents="all" cursor="pointer">
                &gt;
            </text>
        );
    };

    /** OL `placement: 'line'`, `textBaseline: 'bottom'`, white background; non-labels hide when they overflow the path. */
    renderText = (annotation, shape, style) => {
        const ring = shape.kind === 'polygon' ? shape.rings[0] : null;
        const path = shape.kind === 'line' ? shape.points : ring?.length ? [...ring, ring[0]] : null;
        const anchor = pathMidpoint(path);
        if (!anchor) return null;
        const fontSize = style.isLabel ? this.labelFontSize : 20;
        const font = `bold ${fontSize}px ${TEXT_FONT_FAMILY}`;
        const textWidth = measureTextWidth(annotation.title, font);
        if (!style.isLabel && textWidth > pathLength(path)) return null;
        const angle = uprightAngle(anchor.angle) * 180 / Math.PI;
        const padding = 2;
        return (
            <g transform={`translate(${anchor.x},${anchor.y}) rotate(${angle})`} pointerEvents="all" cursor="pointer">
                <rect x={-textWidth / 2 - padding} y={-fontSize - padding} width={textWidth + 2 * padding}
                      height={fontSize + 2 * padding} fill="#ffffff"/>
                <text x={0} y={0} fill={style.color} fontSize={fontSize} fontWeight="bold" fontFamily={TEXT_FONT_FAMILY}
                      textAnchor="middle" dominantBaseline="text-after-edge"
                      stroke={style.isLabel ? 'rgba(255, 255, 255, 0.5)' : undefined}
                      strokeWidth={style.isLabel ? 10 : undefined} paintOrder="stroke" style={{userSelect: 'none'}}>
                    {annotation.title}
                </text>
            </g>
        );
    };

    renderAnnotation = (annotation) => {
        const bounds = parseBounds(annotation);
        if (!bounds) return null;
        const shape = this.project(annotation, bounds);
        if (!shape) return null;

        const isSelected = this._isSelected(annotation.id);
        const style = this.getStyle(annotation, isSelected);
        const transparent = 'rgba(255, 0, 0, 0)';
        const strokeColor = style.isLabel ? transparent : style.color;
        const filled = shape.kind !== 'line';
        const hitProps = {
            stroke: HIT_PAINT,
            strokeWidth: Math.max(HIT_STROKE_WIDTH, style.width + 12),
            fill: filled ? HIT_PAINT : 'none',
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            pointerEvents: filled ? 'all' : 'stroke',
            cursor: 'pointer',
        };
        const layers = [];
        if (!style.isLabel && style.haloWidth > 0) layers.push({stroke: '#fff', strokeWidth: style.haloWidth});
        if (isSelected && !style.isLabel) {
            const {COLOR_SECONDARY, WIDTH_SECONDARY, COLOR_PRIMARY, WIDTH_PRIMARY} = AnnotationsConstants.SELECTED_ANNOTATION;
            layers.push({stroke: COLOR_SECONDARY, strokeWidth: WIDTH_SECONDARY});
            layers.push({stroke: COLOR_PRIMARY, strokeWidth: WIDTH_PRIMARY});
        } else {
            layers.push({stroke: strokeColor, strokeWidth: style.width, strokeDasharray: style.dashed ? '4 6' : undefined});
        }

        return (
            <g key={annotation.id} data-annotation-id={annotation.id}>
                {!style.isLabel && this.renderShape(shape, hitProps)}
                {layers.map((layer, idx) =>
                    <React.Fragment key={idx}>
                        {this.renderShape(shape, {fill: 'none', pointerEvents: 'none', ...layer})}
                    </React.Fragment>)}
                {style.isArrow && shape.kind === 'line' && this.renderArrowHead(shape.points, annotation.color)}
                {style.showText && this.renderText(annotation, shape, style)}
            </g>
        );
    };

    render() {
        const {slide, mapId, activeDrawTool} = this.props;
        const viewer = this._getViewer();
        if (!viewer?.container) return null;

        let w = this.state.containerWidth;
        let h = this.state.containerHeight;
        const el = this.overlayDivRef.current;
        if ((w <= 0 || h <= 0) && el) {
            w = Math.round(el.clientWidth || el.offsetWidth || 0);
            h = Math.round(el.clientHeight || el.offsetHeight || 0);
        }
        const drawing = this.isDrawingHere();
        const clipId = `anno-clip-${mapId}`;

        return ReactDOM.createPortal(
            <div ref={this.overlayDivRef} style={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                pointerEvents: drawing ? 'auto' : 'none', zIndex: 10,
            }}>
                {viewer.viewport && w > 0 && h > 0 && (
                    <svg width={w} height={h} style={{position: 'absolute', top: 0, left: 0}}>
                        <defs>
                            <clipPath id={clipId}><rect x={0} y={0} width={w} height={h}/></clipPath>
                        </defs>
                        <g ref={this.annotationsGroupRef} clipPath={`url(#${clipId})`}
                           style={{pointerEvents: drawing ? 'none' : 'auto'}}>
                            {this.getAnnotations().map(this.renderAnnotation)}
                        </g>
                        {drawing && slide && (
                            <g clipPath={`url(#${clipId})`}>
                                <DrawInteractionOSD
                                    activeTool={activeDrawTool}
                                    viewer={viewer}
                                    slide={slide}
                                    mapId={mapId}
                                    annotations={this.getAnnotations()}
                                    selectedDrawColor={this.props.selectedDrawColor}
                                    selectedAnnoWidth={this.props.selectedAnnoWidth}
                                    toScreen={this.toScreen}
                                    toOl={this.toOl}
                                    containerWidth={w}
                                    containerHeight={h}
                                    tick={this.state.tick}
                                />
                            </g>
                        )}
                    </svg>
                )}
            </div>,
            viewer.container
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const {activeMapId, activeDrawTool, selectedDrawColor, selectedAnnoWidth} = state.gammaStateReducer;
    const mapState = state.mapsStateReducer[ownProps.mapId] || {};
    return {
        isActiveMap: activeMapId === ownProps.mapId,
        slidemap: mapState.slidemap,
        slide: mapState.slide,
        annotations: mapState.annotationState?.annotations,
        selectedAnnotations: mapState.annotationState?.selectedAnnotations,
        activeDrawTool,
        selectedDrawColor: selectedDrawColor || AnnotationsConstants.DEFAULT_ANNO_COLOR,
        selectedAnnoWidth: selectedAnnoWidth || 2,
    };
};

export default connect(mapStateToProps)(AnnotationOverlayOSD);
